import React, { useEffect, useState } from 'react';
import { GoogleLogin, GoogleOAuthProvider, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../../auth/useAuth';
import { fetchNonce } from '../../api/auth';
import mgLogo from '../../assets/mygate_logo.jpg';
import './LoginModal.css';

export default function LoginModal() {
  const { isLoginModalOpen, closeLoginModal, signIn } = useAuth();
  const [nonce, setNonce] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoginModalOpen) return;
    setError(null);
    setNonce(null);
    // eslint-disable-next-line no-console
    console.debug('[auth-debug] fetching nonce...');
    fetchNonce()
      .then((n) => {
        // eslint-disable-next-line no-console
        console.debug('[auth-debug] nonce received, len=', n.length);
        setNonce(n);
      })
      .catch((err) => {
        // Real detail already logged by the publicApi interceptor in
        // api/client.ts — this just marks where in the flow it happened.
        // eslint-disable-next-line no-console
        console.error('[auth-debug] fetchNonce failed', err);
        setError('Unable to reach the sign-in service. Please try again shortly.');
      });
  }, [isLoginModalOpen]);

  if (!isLoginModalOpen) return null;

  const handleSuccess = async (credentialResponse: CredentialResponse) => {
    // eslint-disable-next-line no-console
    console.debug('[auth-debug] GoogleLogin onSuccess', {
      hasCredential: !!credentialResponse.credential,
      credentialLen: credentialResponse.credential?.length,
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    });
    if (!credentialResponse.credential) {
      setError('Sign-in did not return a credential. Please try again.');
      return;
    }
    try {
      await signIn(credentialResponse.credential);
      // eslint-disable-next-line no-console
      console.debug('[auth-debug] signIn succeeded');
      // signIn() closes the modal on success — no navigation. Whatever page
      // triggered the popup (chat header, a gated page's fallback) just
      // re-renders in place now that status is 'authenticated'.
    } catch (err: any) {
      // Real reason (403 body, network error, CORS) is already logged by
      // the `api` interceptor in api/client.ts under "[auth-debug] api failed".
      // eslint-disable-next-line no-console
      console.error('[auth-debug] signIn failed', {
        status: err?.response?.status,
        data: err?.response?.data,
      });
      // The backend intentionally returns the same generic error string for
      // every denial reason (bad token, wrong domain, disabled account, ...).
      setError('Access denied. Please contact your administrator if you believe this is a mistake.');
    }
  };

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';
  if (!clientId) {
    // eslint-disable-next-line no-console
    console.error('[auth-debug] REACT_APP_GOOGLE_CLIENT_ID is empty — button will stay stuck on the '
      + 'placeholder spinner forever. Check this env var was actually baked into the build '
      + '(CRA env vars are compile-time, not runtime — a redeploy without a rebuild won\'t pick up changes).');
  }

  return (
    <div className="login-modal-overlay" onClick={closeLoginModal} role="presentation">
      <div className="login-modal-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="login-modal-close" onClick={closeLoginModal} aria-label="Close">×</button>

        <img className="login-modal-logo" src={mgLogo} alt="Mygate" />
        <h2 className="login-modal-title">MIRA <span>One</span></h2>
        <p className="login-modal-subtitle">Sign in with your @mygate.in account</p>

        {error && <p className="login-modal-error">{error}</p>}

        <div className="login-modal-button-slot">
          {nonce && clientId ? (
            <GoogleOAuthProvider clientId={clientId}>
              <GoogleLogin
                nonce={nonce}
                onSuccess={handleSuccess}
                onError={() => {
                  // eslint-disable-next-line no-console
                  console.error('[auth-debug] GoogleLogin onError — GIS itself rejected the attempt '
                    + '(often: OAuth client\'s Authorized JavaScript origins does not include this origin, '
                    + 'or a browser/third-party-cookie block). Origin=', window.location.origin);
                  setError('Sign-in failed. Please try again.');
                }}
              />
            </GoogleOAuthProvider>
          ) : (
            <div className="login-modal-button-placeholder" aria-busy="true" />
          )}
        </div>

        {/* Placeholder copy — final wording pending HR/policy review. */}
        <p className="login-modal-notice">
          By signing in you agree that your MIRA usage may be reviewed as part of standard reporting.
        </p>
      </div>
    </div>
  );
}
